/**
 * Device Information Collector
 * Collects device details for logging purposes
 * Designed to be non-blocking and performant
 * 
 * Output format matches `clientDeviceInfo` spec in:
 * docs/apis/video-call/booked-event-log-device-info.md
 */

import { getBrowserEnvironment } from 'src/services/mediaPermissions/permissionDetector'
import { appVersion } from 'src/consumer/services/index'
import { getIpAddressFromLocalStorage } from 'src/consumer/services/localstorageServices'
import { getOrCreateEmrDeviceId } from 'src/emr/debug/emrDeviceIdentity'

/**
 * Extract browser version from user agent
 * Performance: O(1) regex match, < 1ms
 */
function extractBrowserVersion(userAgent, browserName) {
  const patterns = {
    chrome: /Chrome\/(\d+(?:\.\d+)*)/,
    safari: /Version\/(\d+(?:\.\d+)*)/,
    firefox: /Firefox\/(\d+(?:\.\d+)*)/,
    edge: /Edg\/(\d+(?:\.\d+)*)/,
    opera: /OPR\/(\d+(?:\.\d+)*)/,
    samsung: /SamsungBrowser\/(\d+(?:\.\d+)*)/,
  }
  
  const pattern = patterns[browserName?.toLowerCase()]
  if (pattern) {
    const match = userAgent.match(pattern)
    return match ? match[1] : null
  }
  return null
}

/**
 * Extract OS version from user agent
 * Performance: O(1) regex match, < 1ms
 */
function extractOSVersion(userAgent, osName) {
  const patterns = {
    windows: /Windows NT (\d+(?:\.\d+)*)/,
    mac: /Mac OS X (\d+[._]\d+(?:[._]\d+)*)/,
    ios: /OS (\d+[._]\d+(?:[._]\d+)*)/,
    android: /Android (\d+(?:\.\d+)*)/,
    linux: null, // Linux versions vary too much
  }
  
  const pattern = patterns[osName?.toLowerCase()]
  if (pattern) {
    const match = userAgent.match(pattern)
    if (match) {
      // Normalize version separators (iOS uses underscore)
      return match[1].replace(/_/g, '.')
    }
  }
  
  // Map Windows NT versions to consumer names
  if (osName?.toLowerCase() === 'windows') {
    if (userAgent.includes('Windows NT 10.0')) return '10/11' // Can't distinguish 10 from 11
    if (userAgent.includes('Windows NT 6.3')) return '8.1'
    if (userAgent.includes('Windows NT 6.2')) return '8'
    if (userAgent.includes('Windows NT 6.1')) return '7'
  }
  
  return null
}

/**
 * Detect platform type for logging
 */
function detectPlatform(browserEnv) {
  const userAgent = navigator.userAgent
  
  // Check for webview (native app wrapper)
  if (/wv\)/.test(userAgent) || /WebView/.test(userAgent)) {
    if (browserEnv.os === 'android') return 'android-webview'
    if (browserEnv.os === 'ios') return 'ios-webview'
  }
  
  // PWA
  if (browserEnv.isPWA) return 'pwa'
  
  // Standard web
  return 'web'
}

/**
 * Get device model from user agent (best effort)
 * Note: This is limited - many browsers don't expose device model
 */
function extractDeviceModel(userAgent) {
  // Android devices often include model
  const androidMatch = userAgent.match(/;\s*([^;)]+)\s*Build\//)
  if (androidMatch) return androidMatch[1].trim()
  
  // iOS devices
  if (/iPhone/.test(userAgent)) return 'iPhone'
  if (/iPad/.test(userAgent)) return 'iPad'
  if (/iPod/.test(userAgent)) return 'iPod'
  
  // Mac
  if (/Macintosh/.test(userAgent)) return 'Mac'
  
  return null
}

/**
 * Get device details synchronously in clientDeviceInfo format
 * Performance: All operations are synchronous and fast (< 5ms total)
 * No network calls, no async operations
 * 
 * Note: ipAddress is included if already cached in localStorage (fetched by App.jsx on startup)
 */
export function getDeviceDetails() {
  const browserEnv = getBrowserEnvironment()
  const userAgent = navigator.userAgent
  
  // Get cached IP address from localStorage (if available)
  // This is fetched by App.jsx on startup and cached - no network call here
  const cachedIpAddress = getIpAddressFromLocalStorage()

  return {
    // Stable per-browser-installation id — identifies this device across check-ins
    // and remote-debug command results (a practitioner can be signed in on several)
    deviceId: getOrCreateEmrDeviceId(),
    // Browser info
    browserName: browserEnv.browser,
    browserVersion: extractBrowserVersion(userAgent, browserEnv.browser),
    browserType: 'web', // Always 'web' for browser-based apps
    
    // OS info
    osName: browserEnv.os,
    osVersion: extractOSVersion(userAgent, browserEnv.os),
    
    // Device info
    deviceModel: extractDeviceModel(userAgent),
    deviceType: browserEnv.deviceType,
    
    // User agent (raw)
    userAgent: userAgent,
    
    // PWA detection
    pwa: browserEnv.isPWA,
    
    // Screen dimensions
    screenWidth: window.screen?.width || null,
    screenHeight: window.screen?.height || null,
    viewportWidth: window.innerWidth || null,
    viewportHeight: window.innerHeight || null,
    devicePixelRatio: window.devicePixelRatio || null,
    
    // App info
    appVersion: appVersion || null,
    platform: detectPlatform(browserEnv),
    
    // Network info (cached, no network call)
    ipAddress: cachedIpAddress || null,
  }
}

/**
 * Get full device details (legacy format for backward compatibility)
 * @deprecated Use getDeviceDetails() for new code
 */
export function getDeviceDetailsLegacy() {
  const browserEnv = getBrowserEnvironment()
  
  return {
    browser: browserEnv.browser,
    os: browserEnv.os,
    deviceType: browserEnv.deviceType,
    isPWA: browserEnv.isPWA,
    isSecure: browserEnv.isSecure,
    userAgent: browserEnv.userAgent,
    screenWidth: window.screen?.width || null,
    screenHeight: window.screen?.height || null,
    screenColorDepth: window.screen?.colorDepth || null,
    screenPixelDepth: window.screen?.pixelDepth || null,
    viewportWidth: window.innerWidth || null,
    viewportHeight: window.innerHeight || null,
    devicePixelRatio: window.devicePixelRatio || null,
    language: navigator.language || navigator.userLanguage || null,
    languages: navigator.languages || [],
    platform: navigator.platform || null,
    hardwareConcurrency: navigator.hardwareConcurrency || null,
    maxTouchPoints: navigator.maxTouchPoints || null,
    cookieEnabled: navigator.cookieEnabled || null,
    onLine: navigator.onLine || null,
  }
}

/**
 * Get clientDeviceInfo for booked event logging
 * This is the primary export - returns the exact format expected by the API
 * 
 * Performance: Fully synchronous, no network calls, < 5ms
 */
export function getClientDeviceInfo() {
  return getDeviceDetails()
}

/**
 * Collect device info for logging (optimized for performance)
 * Returns clientDeviceInfo immediately - no async operations needed
 * 
 * @returns {Object} clientDeviceInfo in the format expected by the API
 */
export function collectDeviceInfoForLogging() {
  return {
    clientDeviceInfo: getDeviceDetails(),
  }
}

